import { NAVIGATION_ROUTES } from "../utils/NavigationUtils";
import { TRIVELTA_PRODUCTS } from "../utils/products";

export type RedirectPriorityEntry = {
  /** OR list — same semantics as the shared Sider `hasAnyPermission` helper. */
  permissions: string[];
  route: string;
  /** `requiredProduct` of the destination route in `App.js`. Callers can use
   *  it (via `isEntryReachable`) to skip entries whose product is disabled,
   *  so a fallback redirect never targets a product-gated page that would
   *  immediately bounce again. Absent = destination is not product-gated. */
  product?: string;
  /** `requiredProductFallbackFeature` of the destination route in `App.js`
   *  (the PAMENG-1820 rollout fallback consumed by `isProductActive`). */
  productFallbackFeature?: string;
};

// Product tags mirroring the `requiredProduct` / `requiredProductFallbackFeature`
// pairs used by the destination routes in `App.js` — keep in sync when a route's
// product gate changes.
const OPS = { product: TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM } as const;
const SPORTSBOOK = {
  product: TRIVELTA_PRODUCTS.SPORTSBOOK,
  productFallbackFeature: "sportsbook_v2",
} as const;
const CASINO = {
  product: TRIVELTA_PRODUCTS.CASINO,
  productFallbackFeature: "casino",
} as const;
const POKER = {
  product: TRIVELTA_PRODUCTS.POKER,
  productFallbackFeature: "poker",
} as const;
const GAME_PASS = {
  product: TRIVELTA_PRODUCTS.GAME_PASS,
  productFallbackFeature: "gamePass",
} as const;

/**
 * Granular per-feature landing rules in Sider order. The first entry whose
 * `permissions` intersects the admin's payload wins, and the admin lands on
 * `route`.
 *
 * `permissions` is an OR list — the same semantics as `hasAnyPermission` in
 * `src/utils/sider/sidebarAccess.js`. The list is intentionally granular:
 * each entry maps to the specific page that its `permissions` actually
 * unlock, matching the `requiredPermission` of the destination route in
 * `App.js`. That way an admin who only holds e.g.
 * `sportsbook.monitoring.sgp_risk` lands directly on
 * `/sportsbook/monitoring/sgp-risk` instead of being routed to a Sportsbook
 * landing they can't access and bounced by `RequireAuth`.
 *
 * Each entry also mirrors the destination route's `requiredProduct` (and
 * rollout fallback feature) so product-aware callers can skip destinations
 * whose product is disabled for the client.
 *
 * Outer order = Sider tab order (KPIs → Users → Sportsbook → Casino →
 * Poker → Game Pass → Promotions → Payments → Compliance → Staff
 * Management). Predictions is sider-only WIP (dev) with no routes yet.
 * Inner order within a tab mirrors the dropdown order so admins with
 * multiple permissions in the same section land on the topmost child.
 *
 * Per-tab "umbrella" entries at the end of each section are defensive
 * last resorts for permissions the BE rarely ships alone (`sportsbook`,
 * `casino`, `compliance`) — they may still bounce in `RequireAuth` if the
 * admin lacks the specific child key, since those bare keys don't gate
 * any individual route.
 *
 * Consumed by:
 *  - `RedirectIfAuthenticated` to pick the post-login landing.
 *  - `protectedElement` to redirect authenticated admins away from
 *    `developmentMode: true` routes when they're not in a dev/test env.
 *  - `GuardFallbackRedirect` to pick a reachable landing after a
 *    route-mode guard failure.
 */
export const REDIRECT_PRIORITY: ReadonlyArray<RedirectPriorityEntry> = [
  // ─── KPIs ─── single page accepts the OR of all kpis sub-keys
  // (kept aligned with the `requiredPermission` array of `/kpis` in
  // `App.js` and with the Sider tab gate, so any admin who can see the
  // KPIs tab also lands on it after login).
  {
    permissions: [
      "kpis",
      "kpis.overview",
      "kpis.financials",
      "kpis.acquisition",
      "kpis.sportsbook",
      "kpis.casino",
    ],
    route: NAVIGATION_ROUTES.KPIS,
    ...OPS,
  },

  // ─── Users ───
  { permissions: ["users"], route: NAVIGATION_ROUTES.USERS, ...OPS },

  // ─── Sportsbook ─── (Fieldbook → Monitoring → Reporting → Configuration)
  {
    permissions: ["sportsbook.fieldbook.events_list"],
    route: NAVIGATION_ROUTES.FIELDBOOK_EVENTS_LIST,
    ...SPORTSBOOK,
  },
  {
    permissions: ["sportsbook.monitoring.latest_bets_list"],
    route: NAVIGATION_ROUTES.MONITORING_LATEST_BETS_LIST,
    ...SPORTSBOOK,
  },
  {
    permissions: ["sportsbook.monitoring.sgp_risk"],
    route: NAVIGATION_ROUTES.SGP_RISK,
    ...SPORTSBOOK,
  },
  {
    permissions: ["sportsbook.reporting.reporting_summary"],
    route: NAVIGATION_ROUTES.REPORTING_SUMMARY,
    ...SPORTSBOOK,
  },
  {
    permissions: ["sportsbook.reporting.top_users"],
    route: NAVIGATION_ROUTES.SPORTSBOOK_TOP_USERS,
    ...SPORTSBOOK,
  },
  {
    permissions: ["sportsbook.configuration.bet_limits"],
    route: NAVIGATION_ROUTES.CONFIGURATION_BET_LIMITS,
    ...SPORTSBOOK,
  },
  {
    permissions: ["sportsbook.configuration.sgp_limits"],
    route: NAVIGATION_ROUTES.SPORTSBOOK_LIMITS,
    ...SPORTSBOOK,
  },
  {
    permissions: ["sportsbook.configuration.account_config"],
    route: NAVIGATION_ROUTES.CONFIGURATION_ACCOUNT_CONFIG,
    ...SPORTSBOOK,
  },
  {
    permissions: ["sportsbook.configuration.early_payout"],
    route: NAVIGATION_ROUTES.CONFIGURATION_EARLY_PAYOUT,
    ...SPORTSBOOK,
  },
  {
    permissions: ["sportsbook.configuration.sportsbook_fm"],
    route: NAVIGATION_ROUTES.SPORTSBOOK_FM,
    ...SPORTSBOOK,
  },
  // Sportsbook umbrella — defensive last resort.
  {
    permissions: ["sportsbook"],
    route: NAVIGATION_ROUTES.FIELDBOOK_EVENTS_LIST,
    ...SPORTSBOOK,
  },

  // ─── Casino ─── (Reports → Configuration)
  {
    permissions: ["casino.reports.finance"],
    route: NAVIGATION_ROUTES.CASINO_FINANCE_REPORTS,
    ...CASINO,
  },
  {
    permissions: ["casino.reports.providers"],
    route: NAVIGATION_ROUTES.CASINO_REPORTS_PROVIDERS,
    ...CASINO,
  },
  {
    permissions: ["casino.reports.games"],
    route: NAVIGATION_ROUTES.CASINO_REPORTS_GAMES,
    ...CASINO,
  },
  {
    permissions: ["casino.reports.top_users"],
    route: NAVIGATION_ROUTES.CASINO_TOP_USERS,
    ...CASINO,
  },
  {
    permissions: ["casino.configuration.games"],
    route: NAVIGATION_ROUTES.CASINO_CONFIGURATION_GAMES,
    ...CASINO,
  },
  {
    permissions: ["casino.configuration.providers"],
    route: NAVIGATION_ROUTES.CASINO_CONFIGURATION_PROVIDERS,
    ...CASINO,
  },
  {
    permissions: ["casino.configuration.collections"],
    route: NAVIGATION_ROUTES.CASINO_CONFIGURATION_CATALOGS,
    ...CASINO,
  },
  {
    permissions: ["casino.configuration.views"],
    route: NAVIGATION_ROUTES.CASINO_CONFIGURATION_VIEWS,
    ...CASINO,
  },
  {
    permissions: ["casino.configuration.tags"],
    route: NAVIGATION_ROUTES.CASINO_CONFIGURATION_TAGS,
    ...CASINO,
  },
  {
    permissions: ["casino.configuration.preview"],
    route: NAVIGATION_ROUTES.CASINO_CONFIGURATION_PREVIEW,
    ...CASINO,
  },
  // Casino umbrellas — defensive last resort.
  {
    permissions: [
      "casino",
      "casino.configuration",
      "casino.configuration.uploads",
    ],
    route: NAVIGATION_ROUTES.CASINO_FINANCE_REPORTS,
    ...CASINO,
  },

  // ─── Poker ─── (Configuration → Reports)
  {
    permissions: ["poker.configuration.cash_game_limits"],
    route: NAVIGATION_ROUTES.POKER_CONFIGURATION_CASH_GAME_LIMITS,
    ...POKER,
  },
  {
    permissions: ["poker.configuration.stakes"],
    route: NAVIGATION_ROUTES.POKER_CONFIGURATION_STAKES,
    ...POKER,
  },
  {
    permissions: ["poker.configuration.private_games"],
    route: NAVIGATION_ROUTES.POKER_CONFIGURATION_PRIVATE_GAMES,
    ...POKER,
  },
  {
    permissions: ["poker.configuration.tournaments"],
    route: NAVIGATION_ROUTES.POKER_CONFIGURATION_TOURNAMENTS,
    ...POKER,
  },
  {
    permissions: ["poker.configuration.timebank"],
    route: NAVIGATION_ROUTES.POKER_CONFIGURATION_TIMEBANK,
    ...POKER,
  },
  {
    permissions: ["poker.configuration.features"],
    route: NAVIGATION_ROUTES.POKER_CONFIGURATION_FEATURES,
    ...POKER,
  },
  {
    permissions: ["poker.reporting.top_users"],
    route: NAVIGATION_ROUTES.POKER_TOP_USERS,
    ...POKER,
  },
  {
    permissions: ["poker"],
    route: NAVIGATION_ROUTES.POKER_CONFIGURATION_CASH_GAME_LIMITS,
    ...POKER,
  },

  // ─── Game Pass ─── (Daily is still WIP with no route, so it lands on
  //  Seasons. Monthly is GA — FE-2640.)
  {
    permissions: [
      "game_pass.configuration.seasons",
      "game_pass.configuration.daily",
    ],
    route: NAVIGATION_ROUTES.GAME_PASS_CONFIGURATION_SEASONS,
    ...GAME_PASS,
  },
  {
    permissions: ["game_pass.configuration.monthly"],
    route: NAVIGATION_ROUTES.GAME_PASS_CONFIGURATION_MONTHLY,
    ...GAME_PASS,
  },
  {
    permissions: ["game_pass.configuration.style"],
    route: NAVIGATION_ROUTES.GAME_PASS_CONFIGURATION_STYLE,
    ...GAME_PASS,
  },

  // ─── Promotions ───
  {
    permissions: ["promotions.bonus_controls"],
    route: NAVIGATION_ROUTES.PROMOTIONS_MANAGEMENT,
    ...OPS,
  },
  {
    permissions: ["promotions.affiliates"],
    route: NAVIGATION_ROUTES.MARKETING_AFFILIATES,
    ...OPS,
  },
  {
    permissions: ["promotions.utm_reports"],
    route: NAVIGATION_ROUTES.UTM_REPORTS,
    ...OPS,
  },
  {
    permissions: ["promotions.campaigns"],
    route: NAVIGATION_ROUTES.DYNAMIC_BONUS_CAMPAIGNS,
    ...OPS,
  },
  {
    permissions: ["promotions.templates"],
    route: NAVIGATION_ROUTES.DYNAMIC_BONUS_TEMPLATES,
    ...OPS,
  },
  {
    permissions: ["promotions.segments"],
    route: NAVIGATION_ROUTES.DYNAMIC_BONUS_SEGMENTS,
    ...OPS,
  },

  // ─── Payments ───
  {
    permissions: ["payments.reports.overview"],
    route: NAVIGATION_ROUTES.PAYMENT_REPORTING,
    ...OPS,
  },
  {
    permissions: ["payments.reports.report_generator"],
    route: NAVIGATION_ROUTES.TRANSACTIONS_REPORTS,
    ...OPS,
  },
  {
    permissions: ["transactions.wallet"],
    route: NAVIGATION_ROUTES.TRANSACTIONS_WALLET,
    ...OPS,
  },
  {
    permissions: ["payments.configuration.routing"],
    route: NAVIGATION_ROUTES.TRANSACTIONS_ROUTING,
    ...OPS,
  },
  {
    permissions: ["payments.configuration.delayed_payment"],
    route: NAVIGATION_ROUTES.DELAYED_PAYMENT,
    ...OPS,
  },

  // ─── Compliance ───
  {
    permissions: ["compliance.fraud_detection"],
    route: NAVIGATION_ROUTES.FRAUD_DETECTION,
    ...OPS,
  },
  {
    permissions: ["transactions.withdrawal_review"],
    route: NAVIGATION_ROUTES.WITHDRAWAL_REVIEW,
    ...OPS,
  },
  {
    permissions: ["compliance"],
    route: NAVIGATION_ROUTES.CONFIGURATIONS_KYC,
    ...OPS,
  },

  // ─── Staff Management ─── (single page, all sub-keys land here)
  {
    permissions: [
      "staff_management",
      "staff_management.staff",
      "staff_management.roles",
    ],
    route: NAVIGATION_ROUTES.STAFF_MANAGEMENT_NEW,
    ...OPS,
  },
  {
    permissions: [
      "staff_management.agent_audit_log",
      "staff_management.agent_audit_log.wallet",
      "staff_management.agent_audit_log.suspensions",
      "staff_management.agent_audit_log.promos",
      "staff_management.agent_audit_log.sports_bets",
      "staff_management.agent_audit_log.withdrawals",
    ],
    route: NAVIGATION_ROUTES.AGENT_AUDIT_LOG,
    ...OPS,
  },
];

export type FindFirstMatchingRouteOptions = {
  /** Skip entries pointing at this route (the caller's current pathname),
   *  so a page that just rejected the admin is never its own redirect
   *  target — that would loop. */
  excludeRoute?: string;
  /** Skip entries the caller knows are unreachable (e.g. the destination's
   *  product is disabled). Kept as a caller-supplied predicate so this
   *  module stays pure — no FeatureFlagService import — and testable. */
  isEntryReachable?: (entry: RedirectPriorityEntry) => boolean;
};

/**
 * Walks `REDIRECT_PRIORITY` and returns the route of the first entry whose
 * `permissions` intersects the admin's payload. Returns `null` when the
 * admin holds no permission recognised by the Sider — caller decides how
 * to handle that (sign out vs. fallback route).
 */
export const findFirstMatchingRoute = (
  adminPermissions: readonly string[] | null | undefined,
  { excludeRoute, isEntryReachable }: FindFirstMatchingRouteOptions = {},
): string | null => {
  if (!Array.isArray(adminPermissions) || adminPermissions.length === 0) {
    return null;
  }
  const owned = new Set(adminPermissions);
  for (const entry of REDIRECT_PRIORITY) {
    if (excludeRoute !== undefined && entry.route === excludeRoute) {
      continue;
    }
    if (isEntryReachable && !isEntryReachable(entry)) {
      continue;
    }
    if (entry.permissions.some((p) => owned.has(p))) {
      return entry.route;
    }
  }
  return null;
};
